import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Trash2, Pencil } from "lucide-react";
import { REBATE_MEDIA_OWNERS, TIER_ACCENT, TIER_LABEL, type RebateTier } from "@/config/affiliateRebates";

interface RewardRow {
  id: string;
  media_owner_name: string;
  reward_type: "screens" | "discount";
  tier: RebateTier;
  quantity: number;
  quantity_used: number;
  duration_weeks: number;
  granted_at: string;
  expires_at: string;
  status: "active" | "fully_used" | "expired" | "revoked";
}

interface Props {
  open: boolean;
  onClose: () => void;
  affiliateUserId: string;
  affiliateName: string;
  period: { id: string } | null;
  onChanged: () => void;
}

export const RebatesRewardsDrawer = ({
  open,
  onClose,
  affiliateUserId,
  affiliateName,
  period,
  onChanged,
}: Props) => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const isAr = language === "ar";
  const [rewards, setRewards] = useState<RewardRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("affiliate_rebate_rewards")
      .select("*")
      .eq("affiliate_user_id", affiliateUserId)
      .order("granted_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setRewards((data ?? []) as RewardRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, affiliateUserId]);

  const updateUsed = async (id: string, used: number) => {
    const r = rewards.find((x) => x.id === id);
    if (!r) return;
    const newStatus = used >= r.quantity ? "fully_used" : "active";
    const { error } = await supabase
      .from("affiliate_rebate_rewards")
      .update({ quantity_used: used, status: newStatus })
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    await load();
    onChanged();
  };

  const revoke = async (id: string) => {
    if (!confirm(isAr ? "إلغاء هذه المكافأة؟" : "Revoke this reward?")) return;
    await supabase
      .from("affiliate_rebate_rewards")
      .update({ status: "revoked" })
      .eq("id", id);
    await load();
    onChanged();
  };

  const grantManual = async (ownerName: string, tier: RebateTier) => {
    if (!period) {
      toast({
        title: isAr ? "إنهاء الربع أولاً" : "No period yet",
        description: isAr
          ? "يجب حساب المبيعات أولاً لإنشاء سجل ربع."
          : "Recompute sales first to create a period record.",
        variant: "destructive",
      });
      return;
    }
    const owner = REBATE_MEDIA_OWNERS.find((o) => o.name === ownerName);
    if (!owner) return;
    const reward = owner.screenRewards[tier];
    await supabase.from("affiliate_rebate_rewards").insert({
      period_id: period.id,
      affiliate_user_id: affiliateUserId,
      media_owner_name: ownerName,
      reward_type: "screens",
      tier,
      quantity: reward.screens,
      duration_weeks: reward.durationWeeks,
    });
    await load();
    onChanged();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{affiliateName}</SheetTitle>
          <SheetDescription>
            {isAr ? "كل مكافآت هذا الشريك حسب مالك الوسائط." : "All rewards for this affiliate, grouped by media owner."}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground text-sm">{isAr ? "جاري التحميل..." : "Loading..."}</div>
        ) : (
          <div className="mt-6 space-y-6">
            {REBATE_MEDIA_OWNERS.map((owner) => {
              const ownerRewards = rewards.filter((r) => r.media_owner_name === owner.name);
              return (
                <div key={owner.name} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-foreground">{owner.name}</h3>
                    <div className="flex gap-1">
                      {(["silver", "gold", "platinum"] as RebateTier[]).map((t) => (
                        <Button
                          key={t}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => grantManual(owner.name, t)}
                        >
                          + {TIER_LABEL[t][isAr ? "ar" : "en"]}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {ownerRewards.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">
                      {isAr ? "لا توجد مكافآت بعد." : "No rewards yet."}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {ownerRewards.map((r) => {
                        const accent = TIER_ACCENT[r.tier];
                        const remaining = Math.max(0, r.quantity - r.quantity_used);
                        return (
                          <div
                            key={r.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2.5 text-sm"
                          >
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={`${accent.text} border-current/30`}>
                                {TIER_LABEL[r.tier][isAr ? "ar" : "en"]}
                              </Badge>
                              <div>
                                <div className="font-medium text-foreground">
                                  {r.reward_type === "screens"
                                    ? `${r.quantity} ${isAr ? "شاشة" : "screens"} · ${r.duration_weeks}w`
                                    : `${r.quantity}% ${isAr ? "خصم" : "discount"}`}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                  {isAr ? "ينتهي" : "expires"}: {new Date(r.expires_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <StatusBadge status={r.status} isAr={isAr} />
                              {r.reward_type === "screens" && r.status === "active" && (
                                <div className="flex items-center gap-1 text-xs">
                                  <Pencil className="h-3 w-3 text-muted-foreground" />
                                  <Input
                                    type="number"
                                    defaultValue={r.quantity_used}
                                    className="h-7 w-16 text-xs"
                                    onBlur={(e) => updateUsed(r.id, Number(e.target.value))}
                                  />
                                  <span className="text-muted-foreground">/ {r.quantity}</span>
                                  <span className="text-muted-foreground">({remaining} left)</span>
                                </div>
                              )}
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-destructive"
                                onClick={() => revoke(r.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const StatusBadge = ({ status, isAr }: { status: RewardRow["status"]; isAr: boolean }) => {
  const map: Record<RewardRow["status"], { label: { en: string; ar: string }; cls: string }> = {
    active: { label: { en: "Active", ar: "نشطة" }, cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
    fully_used: { label: { en: "Used", ar: "مستخدمة" }, cls: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
    expired: { label: { en: "Expired", ar: "منتهية" }, cls: "bg-muted text-muted-foreground border-border" },
    revoked: { label: { en: "Revoked", ar: "ملغاة" }, cls: "bg-destructive/15 text-destructive border-destructive/30" },
  };
  const s = map[status];
  return <Badge variant="outline" className={`text-[10px] ${s.cls}`}>{s.label[isAr ? "ar" : "en"]}</Badge>;
};

export default RebatesRewardsDrawer;
